// Copyright 2021-2023 The Connect Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import {
  type DescMessage,
  type DescMethodUnary,
  type MessageInitShape,
  type MessageValidType,
  create,
} from '@bufbuild/protobuf';
import { type ConnectError, createContextValues, type Transport } from '@connectrpc/connect';
import {
  // oxlint-disable-next-line no-restricted-imports
  createConnectQueryKey,
  useTransport,
} from '@connectrpc/connect-query';
import {
  type QueryClient,
  type UseMutationResult,
  type UseMutationOptions as TSUseMutationOptions,
  useMutation as useMutationTs,
  type MutationFunctionContext,
} from '@tanstack/react-query';
import { type AnyRouter, useRouter } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { callUnaryMethod } from '../call-unary-method';
import { ctxKeyQueryClient } from '../context-keys';
import { type DeepOmitProtobufInternals, type FieldMaskUpdateVariables } from '../types';

type MessageInputShape<I extends DescMessage> = DeepOmitProtobufInternals<MessageValidType<I>>;
type MessageOutputShape<O extends DescMessage> = MessageValidType<O>;

/**
 * Variables accepted by a mutation: FieldMask-aware for `Update*Request`s
 * (partial resource plus a checked mask), the plain request shape otherwise.
 */
export type MutationVariables<I extends DescMessage> = FieldMaskUpdateVariables<MessageInputShape<I>>;

/**
 * A flexible factory type that works with specific schema types.
 * Pass the schema type and context type to get a properly typed hook function.
 */
export type MutationFactory<TSchema, TContext = unknown, TOnMutateResult = unknown> =
  TSchema extends DescMethodUnary<infer I, infer O>
    ? (
        options?: UseMutationOptions<I, O, TContext, TOnMutateResult>,
      ) => UseMutationResult<
        MessageOutputShape<O>,
        ConnectError,
        MutationVariables<I>,
        TContext & { router: AnyRouter }
      >
    : never;

type UseMutationOptionsInner<I extends DescMessage, O extends DescMessage, TOnMutateResult> = TSUseMutationOptions<
  MessageOutputShape<O>,
  ConnectError,
  MutationVariables<I>,
  TOnMutateResult
> & {
  queryClient?: QueryClient;
  transport?: Transport;
};

type UseMutationOptions<
  I extends DescMessage,
  O extends DescMessage,
  TOnMutateResult,
  TContext = MutationFunctionContext,
> = Omit<
  UseMutationOptionsInner<I, O, TOnMutateResult & { router: AnyRouter }>,
  'mutationKey' | 'mutationFn' | 'onMutate'
> & {
  onMutate?: (input: MutationVariables<I>, context: TContext) => Promise<TOnMutateResult> | TOnMutateResult | undefined;
};

/**
 * Query the method provided. This is basically a wrapper for TanStack Query's useMutation().
 *
 * The most important capabilities are:
 * 1. provide the queryClient within contextValues for use within interceptors.
 * 2. provide the router within the onMutate context for easy access within the onSuccess, onError and onSettled callbacks.
 *
 * @see https://github.com/connectrpc/connect-query-es/blob/main/packages/connect-query/src/use-mutation.ts
 */
export function useMutation<I extends DescMessage, O extends DescMessage, TOnMutateResult = unknown>(
  schema: DescMethodUnary<I, O>,
  { onMutate, queryClient, ...mutationOptions }: UseMutationOptions<I, O, TOnMutateResult> = {},
): UseMutationResult<
  MessageOutputShape<O>,
  ConnectError,
  MutationVariables<I>,
  TOnMutateResult & { router: AnyRouter }
> {
  const transportFromCtx = useTransport();
  const { transport: transportFromOption, ...options } = mutationOptions;
  const transport = transportFromOption ?? transportFromCtx;
  const router = useRouter();
  const contextValues = useMemo(() => createContextValues(), []);

  return useMutationTs(
    {
      ...options,
      mutationKey: useMemo(
        () =>
          createConnectQueryKey({
            schema,
            transport,
            cardinality: 'finite',
          }),
        [schema, transport],
      ),
      mutationFn: useCallback(
        async (input: MutationVariables<I>, context: MutationFunctionContext) => {
          const body = create(schema.input, input as MessageInitShape<I>);
          const result = await callUnaryMethod(transport, schema, body, {
            contextValues: contextValues.set(ctxKeyQueryClient, context.client),
          });
          return result as MessageOutputShape<O>;
        },
        [transport, schema, contextValues],
      ),
      onMutate: useCallback(
        async (input: MutationVariables<I>, context: MutationFunctionContext) => {
          const result = await onMutate?.(input, context);
          return { ...result, router } as TOnMutateResult & { router: AnyRouter };
        },
        [onMutate, router],
      ),
    },
    queryClient,
  );
}
